import { Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Optional, Output, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { LoggerFactory } from '@elderbyte/ts-logger';
import { RequestDefinitionService } from '../../providers/request-definition.service';
import { RequestDefinition } from '../../model/request-definition';
import {MatFormFieldControl} from '@angular/material/form-field';

@Component({
  selector: 'tcc-request-definition-selector',
  templateUrl: './request-definition-selector.component.html',
  styleUrls: ['./request-definition-selector.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: RequestDefinitionSelectorComponent
    }
  ]
})
export class RequestDefinitionSelectorComponent implements OnInit, OnDestroy, MatFormFieldControl<RequestDefinition>, ControlValueAccessor {

  static nextId = 0;

  _logger = LoggerFactory.getLogger(this.constructor.name);

  _placeholder = '';
  _disabled = false;

  _requestDefinitions: RequestDefinition[] = [];

  @HostBinding('attr.aria-describedby') _describedBy = '';
  @HostBinding() id = `tcc-request-definition-selector-${RequestDefinitionSelectorComponent.nextId++}`;

  shouldPlaceholderFloat: boolean;
  focused: boolean;
  _required: boolean;
  _stateChanges: Subject<void> = new Subject<void>();
  _value: RequestDefinition | null;

  @Output() selectRequestDefinition = new EventEmitter<RequestDefinition>();

  _onChange: any = (val: any) => {};
  _onTouched: any = () => {};

  constructor(private requestDefinitionService: RequestDefinitionService,
              @Optional() @Self() public ngControl: NgControl,
              private fm: FocusMonitor,
              private elRef: ElementRef) {
    ngControl.valueAccessor = this;

    // Monitor element focus
    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }


  ngOnInit() {
    this.loadRequestDefinitions();
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }


  /**
   * Refresh the list of request definitions
   */
  loadRequestDefinitions() {
    this.requestDefinitionService.getAvailableRequestDefinitions()
      .subscribe(
        (requestDefinitions: RequestDefinition[]) => {
          this._logger.debug(`Successfully received a list of available request definitions: ${JSON.stringify(requestDefinitions)}`);
          this._requestDefinitions = requestDefinitions;
        },
        (error: any) => {
          this._logger.error(`Failed to get available request definitions: ${JSON.stringify(error)}`);
          this._requestDefinitions = [];
        }
    );
  }

  onSelect(event) {
    this.selectRequestDefinition.emit(this.value);
  }


  get value() {
    return this._value;
  }
  set value(value: RequestDefinition) {
    this._value = value;
    this.stateChanges.next();
    this._onChange(value);
    this._onTouched();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(placeholder: string) {
    this._placeholder = placeholder;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(required: boolean) {
    this._required = required;
    this.stateChanges.next();
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(disabled: boolean) {
    this._disabled = disabled;
    this.stateChanges.next();
  }

  get empty() {
    return !this.value;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState() {
    return !!this.ngControl.errors;
  }

  get controlType() {
    return 'tcc-request-definition-selector';
  }

  get describedBy() {
    return this._describedBy;
  }
  set describedBy(describedBy: string) {
    this._describedBy = describedBy;
  }

  get stateChanges() {
    return this._stateChanges;
  }
  set stateChanges(stateChanges: Subject<void>) {
    this._stateChanges = stateChanges;
  }


  // MatFormFieldControl implementation

  onContainerClick(event: MouseEvent): void {
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }


  // ControlValueAccessor implementation

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

}
